import React from 'react';
import { Outlet } from 'react-router-dom';
import {
  Box,
  Button,
  Drawer,
  Typography,
  Divider,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Search as LucideSearch,
  LayoutDashboard,
  Heart,
  Bookmark,
  User,
  LogOut,
  Bell,
  CreditCard,
  X,
  Sun,
  Moon,
} from 'lucide-react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import Footer from './Footer';

const HEADER_HEIGHT = 56;
const drawerWidth = 280;

// Inline nav link for desktop header
const NavLink = ({ to, label, active, onClick, isDark }) => (
  <RouterLink to={to} onClick={onClick} style={{ textDecoration: 'none' }}>
    <Typography
      sx={{
        fontSize: '16px',
        fontWeight: active ? 600 : 450,
        color: active ? (isDark ? '#F1F5F9' : '#0a0a0a') : isDark ? '#60a5fa' : '#046AFF',
        px: 1.25,
        py: 0.5,
        borderRadius: '6px',
        transition: 'all 120ms ease',
        whiteSpace: 'nowrap',
        '&:hover': {
          color: active ? (isDark ? '#F1F5F9' : '#0a0a0a') : isDark ? '#93bbfc' : '#0355d6',
          bgcolor: isDark ? 'rgba(96, 165, 250, 0.14)' : 'rgba(4, 106, 255, 0.14)',
        },
      }}
    >
      {label}
    </Typography>
  </RouterLink>
);

// Drawer nav item for mobile
const DrawerNavItem = ({ icon, label, path, active, onClick }) => (
  <RouterLink to={path} onClick={onClick} style={{ textDecoration: 'none', color: 'inherit' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.5,
        py: 1.25,
        mx: 1,
        borderRadius: '8px',
        fontSize: '0.9375rem',
        fontWeight: active ? 600 : 450,
        color: active ? 'text.primary' : 'text.secondary',
        bgcolor: active ? 'action.selected' : 'transparent',
        transition: 'all 120ms ease',
        '&:hover': {
          bgcolor: 'action.hover',
          color: 'text.primary',
        },
      }}
    >
      {icon}
      {label}
    </Box>
  </RouterLink>
);

function Layout() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { user, userData, isAnonymous, logOut } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const isDark = mode === 'dark';
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const iconProps = { size: 18, strokeWidth: 1.75 };

  const navItems = [
    { text: 'Search', icon: <LucideSearch {...iconProps} />, path: '/browse' },
    // { text: 'Dashboard', icon: <LayoutDashboard {...iconProps} />, path: '/dashboard' }, // shelved — see ROADMAP.md
    { text: 'Favorites', icon: <Heart {...iconProps} />, path: '/favorites' },
    ...(!isAnonymous ? [
      { text: 'Saved Searches', icon: <Bookmark {...iconProps} />, path: '/saved-searches' },
    ] : []),
  ];

  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
  const handleNavClick = () => setDrawerOpen(false);
  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleProfile = () => { handleClose(); navigate('/profile'); };
  const handleSubscription = () => { handleClose(); navigate('/subscription'); };
  const handleLogout = async () => {
    handleClose();
    try { await logOut(); navigate('/'); }
    catch (error) { console.error('Error logging out:', error); }
  };

  // ── Wordmark ──
  const Wordmark = ({ fontSize = '1.3rem' }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        component="img"
        src="https://blueshiftauto.com/icon-blueshift.png"
        alt="BlueShift Auto"
        sx={{
          width: 30,
          height: 30,
          borderRadius: '6px',
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize,
          fontWeight: 700,
          letterSpacing: '-0.03em',
          color: isDark ? '#F1F5F9' : '#0a0a0a',
          whiteSpace: 'nowrap',
        }}
      >
        BlueShift Auto
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          height: HEADER_HEIGHT,
          bgcolor: isDark ? 'rgba(10,14,23,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          display: 'flex',
          alignItems: 'center',
          px: { xs: 1.5, sm: 2.5 },
        }}
      >
        {isMobile ? (
          /* ── Mobile header: hamburger | centered wordmark | icons ── */
          <>
            <IconButton onClick={handleDrawerToggle} sx={{ color: isDark ? '#9ca3af' : '#374151', p: 1 }}>
              <MenuIcon size={22} strokeWidth={1.75} />
            </IconButton>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <Wordmark />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <IconButton onClick={toggleMode} sx={{ color: '#9ca3af', p: 1 }} title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
                {isDark ? <Sun size={18} strokeWidth={1.75} /> : <Moon size={18} strokeWidth={1.75} />}
              </IconButton>
              {isAnonymous ? (
                <Button
                  component={RouterLink}
                  to="/login"
                  size="small"
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: isDark ? '#9ca3af' : '#374151',
                    minWidth: 'auto',
                    px: 1.5,
                  }}
                >
                  Sign In
                </Button>
              ) : (
                <>
                  <IconButton sx={{ color: '#9ca3af', p: 1 }}>
                    <Bell size={18} strokeWidth={1.75} />
                  </IconButton>
                  <IconButton onClick={handleMenu} sx={{ p: 0.5 }}>
                    <Avatar
                      alt={userData?.displayName || user?.email}
                      src={userData?.photoURL}
                      sx={{
                        width: 30,
                        height: 30,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        bgcolor: isDark ? '#60a5fa' : '#046AFF',
                        color: '#fff',
                      }}
                    >
                      {(userData?.displayName?.[0] || user?.email?.[0] || '').toUpperCase()}
                    </Avatar>
                  </IconButton>
                </>
              )}
            </Box>
          </>
        ) : (
          /* ── Desktop header: nav links | centered wordmark | right actions ── */
          <>
            {/* Left nav links */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  label={item.text}
                  active={location.pathname === item.path}
                  isDark={isDark}
                />
              ))}
            </Box>

            {/* Centered wordmark */}
            <RouterLink to="/" style={{ textDecoration: 'none' }}>
              <Wordmark />
            </RouterLink>

            {/* Right actions */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
              {isAnonymous ? (<>
                <IconButton onClick={toggleMode} sx={{ color: '#9ca3af', p: 1 }} title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
                  {isDark ? <Sun size={18} strokeWidth={1.75} /> : <Moon size={18} strokeWidth={1.75} />}
                </IconButton>
                <Button
                  variant="contained"
                  component={RouterLink}
                  to="/login"
                  size="small"
                  sx={{
                    bgcolor: isDark ? '#60a5fa' : '#046AFF',
                    color: '#fff',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    px: 2,
                    py: 0.625,
                    borderRadius: '6px',
                    '&:hover': { bgcolor: isDark ? '#93bbfc' : '#0355d6' },
                  }}
                >
                  Sign In
                </Button>
              </>) : (
                <>
                  {!isAnonymous && (
                    <NavLink
                      to="/subscription"
                      label="Subscription"
                      active={location.pathname === '/subscription'}
                      isDark={isDark}
                    />
                  )}
                  <IconButton onClick={toggleMode} sx={{ color: '#9ca3af', p: 1 }} title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
                    {isDark ? <Sun size={18} strokeWidth={1.75} /> : <Moon size={18} strokeWidth={1.75} />}
                  </IconButton>
                  <IconButton sx={{ color: '#9ca3af', p: 1 }}>
                    <Bell size={18} strokeWidth={1.75} />
                  </IconButton>
                  <IconButton onClick={handleMenu} sx={{ p: 0.5, ml: 0.25 }}>
                    <Avatar
                      alt={userData?.displayName || user?.email}
                      src={userData?.photoURL}
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        bgcolor: isDark ? '#60a5fa' : '#046AFF',
                        color: '#fff',
                        border: '2px solid rgba(37,99,235,0.2)',
                      }}
                    >
                      {(userData?.displayName?.[0] || user?.email?.[0] || '').toUpperCase()}
                    </Avatar>
                  </IconButton>
                </>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* ── User dropdown menu ── */}
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{ paper: { sx: { minWidth: 180, mt: 0.5, py: 0.5 } } }}
      >
        <MenuItem onClick={handleProfile} sx={{ gap: 1.5 }}>
          <User size={16} strokeWidth={1.75} color="#6b7280" />
          <Typography variant="body2" sx={{ color: 'text.primary' }}>Profile</Typography>
        </MenuItem>
        <MenuItem onClick={handleSubscription} sx={{ gap: 1.5 }}>
          <CreditCard size={16} strokeWidth={1.75} color="#6b7280" />
          <Typography variant="body2" sx={{ color: 'text.primary' }}>Subscription</Typography>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleLogout} sx={{ gap: 1.5 }}>
          <LogOut size={16} strokeWidth={1.75} color="#6b7280" />
          <Typography variant="body2" sx={{ color: 'text.primary' }}>Log out</Typography>
        </MenuItem>
      </Menu>

      {/* ── Mobile drawer ── */}
      <Drawer
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: isDark ? '#111827' : '#ffffff',
          },
        }}
        variant="temporary"
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, height: HEADER_HEIGHT }}>
          <Wordmark />
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#9ca3af', p: 0.75 }}>
            <X size={18} strokeWidth={2} />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 1 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5 }}>
          {navItems.map((item) => (
            <DrawerNavItem
              key={item.text}
              icon={item.icon}
              label={item.text}
              path={item.path}
              active={location.pathname === item.path}
              onClick={handleNavClick}
            />
          ))}
        </Box>

        {!isAnonymous && (
          <>
            <Divider sx={{ my: 1 }} />
            <DrawerNavItem
              icon={<CreditCard {...iconProps} />}
              label="Subscription"
              path="/subscription"
              active={location.pathname === '/subscription'}
              onClick={handleNavClick}
            />
            {userData?.subscription_tier && (
              <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', px: 3.5, mt: 0.25 }}>
                {userData.subscription_tier.charAt(0).toUpperCase() + userData.subscription_tier.slice(1)} plan
              </Typography>
            )}
          </>
        )}
      </Drawer>

      {/* ── Main content ── */}
      <Box component="main" sx={{ flexGrow: 1, p: 3, pt: `${HEADER_HEIGHT + 24}px`, overflowX: 'hidden', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Box sx={{ flex: 1 }}>
          <Outlet />
        </Box>
        {location.pathname !== '/' && <Footer />}
      </Box>
    </Box>
  );
}

export default Layout;
